import { useContext, useEffect, useReducer, useRef } from 'react';
import { ClickToDismissPortalContext, PopoverContext } from './context';

export function useOutsideAlerter(ref: any, handler: any, childRef?: any) {
	useEffect(() => {
		const listener = (event: any) => {
			// Do nothing if clicking ref's element or descendent elements
			if (!ref.current || ref.current.contains(event.target) || childRef?.current.contains(event.target)) {
				return;
			}

			// Check if any ancestor of the clicked element has the class "disable-outside-alerter"
			if (event.target.closest('.disable-outside-alerter')) {
				return;
			}

			handler(event);
		};

		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler]);
}

export const useClickToDismissContext = () => useContext(ClickToDismissPortalContext);

export const useForceRender = () => useReducer(a => !a, false)[1];

export const useContainer = () => {
	const ref = useRef<HTMLElement>();
	const rerender = useForceRender();

	return {
		containerSetter: (r: HTMLElement) => {
			ref.current = r;
			rerender();
		},
		ref
	};
};

export function usePopoverContext() {
	return useContext(PopoverContext);
}

export function useClickToDismiss(handleLastClose?: () => void) {
	const context = useContext(ClickToDismissPortalContext);

	const openedChildIds = useRef<Record<string, boolean | Function>>({}); // = useRef<Record<string, boolean>>({});

	const childrenOpen = () => Object.keys(openedChildIds.current).length > 0;

	const shouldAllowClose = () => {
		// console.log('should allow close', openedChildIds.current, context);
		if (!childrenOpen()) return true;

		// if there are any children open that are not our handler - immediately return false
		if (Object.values(openedChildIds.current).some(a => typeof a === 'boolean')) return false;

		for (const handler of Object.values(openedChildIds.current)) {
			if (typeof handler === 'function') {
				if (handler()) {
					// console.log('handler allowed close');
					return true;
				}
			}
		}
		return false;
	};
	return {
		context,
		openIds: () => Object.keys(openedChildIds.current),
		contextValue: {
			childPortalOpened: (id: string, handler?: boolean | (() => boolean)) => {
				const isHandler = typeof handler === 'function';
				openedChildIds.current[id] = isHandler ? handler : true;
				// console.log('child opened', id, Object.keys(openedChildIds.current), context);
				// handlers are intended to only really be used for dirty state checking / confirmation
				// we will only use the handler if there are no children open so it is important this isn't nested
				// they are not passed up to the parent contexts
				if (context && !isHandler) context.childPortalOpened(id);
				else if (context) {
					// console.log('child opened but handler', id);
				}
			},
			childPortalClosed: (id: string, unmount: boolean) => {
				if (unmount && !openedChildIds.current[id]) return;

				// if (props.debugId) console.log('child closed', popoverId.current, id);
				context?.childPortalClosed(id);

				if (context && openedChildIds.current[id]) {
					if (Object.keys(openedChildIds.current).length === 1) {
						handleLastClose?.();
					}
				}
				delete openedChildIds.current[id];
			}
		},
		childrenOpen,
		shouldAllowClose
	};
}

// Prevent State update on unmounted comp
export function useDidMount() {
	const didMountRef = useRef(true);

	useEffect(() => {
		didMountRef.current = true;
		return () => void (didMountRef.current = false);
	}, []);
	return didMountRef;
}
